import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Divider,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { useReports } from '../../../hooks/useReports';
import axios from '../../../services/ApiService';
import LoadingSpinner from '../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { Invoice, InvoiceFilter } from '../../types/DataTypes';
import DownloadIcon from '@mui/icons-material/Download';

function BusinessPayments() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState<InvoiceFilter>({
    startDate: '',
    endDate: '',
    userType: '',
    paymentType: ''
  });
  const { user } = useAuthContext();
  const { downloadReport } = useReports();

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    filterInvoices();
  }, [invoices, searchTerm, startDate, endDate]);

  const fetchInvoices = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await axios.get('/getPaidInvoicesForCurrentUser');
      setInvoices(response.data);
    } catch (error) {
      toast.error('Error loading invoices');
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterInvoices = () => {
    let filtered = invoices.filter(invoice => {
      const matchesSearch = searchTerm === '' || 
        invoice.invoiceId.toString().includes(searchTerm) ||
        invoice.paymentType.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Date range filtering
      const invoiceDate = new Date(invoice.transactionDate);
      const matchesStartDate = !startDate || invoiceDate >= startDate;
      const matchesEndDate = !endDate || invoiceDate <= endDate;
      
      return matchesSearch && matchesStartDate && matchesEndDate;
    });
    
    setFilteredInvoices(filtered);
  };

  const handleRefresh = () => {
    fetchInvoices();
  };

  const handleExportClick = () => {
    // Pre-fill export filters with current selections
    setExportFilters({
      startDate: startDate?.toISOString() || '',
      endDate: endDate?.toISOString() || '',
      userType: '',
      paymentType: ''
    });
    setExportDialogOpen(true);
  };

  const handleExportConfirm = () => {
    downloadReport('/generateInvoiceReportExcel', exportFilters, 'invoices_report.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    setExportDialogOpen(false);
  };

  const handleExportCancel = () => {
    setExportDialogOpen(false);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <>
      <Helmet>
        <title>BVT Admin - Invoices</title>
      </Helmet>
      {loading ? (
        <LoadingSpinner loading={loading} />
      ) : (
        <Stack spacing={3}>
          <Card>
            <CardHeader 
              title="Invoices"
              action={
                <Stack direction="row" spacing={1}>
                  {/* <Tooltip title="Export to Excel" arrow>
                    <IconButton 
                      onClick={handleExportClick} 
                      disabled={loading}
                      sx={{
                        backgroundColor: 'grey.200',
                        color: 'grey.700',
                        '&:hover': {
                          backgroundColor: 'grey.300',
                        },
                        '&:disabled': {
                          backgroundColor: 'grey.100',
                          color: 'grey.400',
                        }
                      }}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </Tooltip> */}
                  <Tooltip title="Refresh Invoices" arrow>
                    <IconButton 
                      onClick={handleRefresh} 
                      disabled={loading}
                      sx={{
                        backgroundColor: 'grey.200',
                        color: 'grey.700',
                        '&:hover': {
                          backgroundColor: 'grey.300',
                        },
                        '&:disabled': {
                          backgroundColor: 'grey.100',
                          color: 'grey.400',
                        }
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reset Filters" arrow>
                    <IconButton 
                      onClick={handleResetFilters} 
                      disabled={loading}
                      sx={{
                        backgroundColor: 'grey.200',
                        color: 'grey.700',
                        '&:hover': {
                          backgroundColor: 'grey.300',
                        },
                        '&:disabled': {
                          backgroundColor: 'grey.100',
                          color: 'grey.400',
                        }
                      }}
                    >
                      <RestartAltIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            />
            <Divider />
                        <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <TextField
                    placeholder="Search by Invoice ID or Invoice Type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flex: 1 }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DatePicker
                        label="From"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { minWidth: 150 }
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ color: 'text.secondary', mx: 1 }}>
                        to
                      </Typography>
                      <DatePicker
                        label="To"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { minWidth: 150 }
                          }
                        }}
                      />
                    </Stack>
                  </LocalizationProvider>
                </Stack>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice Id</TableCell>
                        <TableCell>Invoice Type</TableCell>
                        <TableCell >Amount</TableCell>
                        <TableCell>Payment Gateway</TableCell>
                        <TableCell>Transaction Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Invoice</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredInvoices.map((invoice) => (
                        <TableRow key={invoice.invoiceId}>
                          <TableCell>{invoice.invoiceId}</TableCell>
                          <TableCell>{invoice.paymentType}</TableCell>
                          <TableCell >{invoice.amount?.toFixed(2)}</TableCell>
                          <TableCell>{invoice.paymentGateway}</TableCell>
                          <TableCell>{new Date(invoice.transactionDate).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.paymentStatus}
                              color={invoice.paymentStatus === 'PAID' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                          <Tooltip title="Download Invoice">
                              <IconButton
                                onClick={() => downloadReport(
                                  '/generateInvoice',
                                  invoice.invoiceId,
                                  `invoice-${invoice.invoiceId}.pdf`
                                )}
                                disabled={loading}
                                size="small"
                                color="primary"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredInvoices.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No invoices found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}
      
      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={handleExportCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Export to Excel</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction="row" spacing={2} alignItems="center">
                <DatePicker
                  label="From Date"
                  value={exportFilters.startDate ? new Date(exportFilters.startDate) : null}
                  onChange={(newValue) => setExportFilters(prev => ({
                    ...prev,
                    startDate: newValue?.toISOString() || ''
                  }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={exportFilters.endDate ? new Date(exportFilters.endDate) : null}
                  onChange={(newValue) => setExportFilters(prev => ({
                    ...prev,
                    endDate: newValue?.toISOString() || ''
                  }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: -2.5 }}>
                Leave the date range blank to export all invoices
              </Typography>
            </LocalizationProvider>
            

          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExportCancel}>Cancel</Button>
          <Button onClick={handleExportConfirm} variant="contained">Export</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BusinessPayments; 